import React from 'react';
import { useTranslation } from 'react-i18next';
import './HoroscopeDisplay.css';

const ZODIAC_SYMBOLS = {
  aries: '♈',
  taurus: '♉',
  gemini: '♊',
  cancer: '♋',
  leo: '♌',
  virgo: '♍',
  libra: '♎',
  scorpio: '♏',
  sagittarius: '♐',
  capricorn: '♑',
  aquarius: '♒',
  pisces: '♓',
};

const SECTION_KEYS = ['general_outlook', 'love_relationships', 'career_finances', 'health_wellbeing'];

// Gentle background tint by score: low (1) = soft amber, high (10) = soft green
function getSectionBackgroundStyle(score) {
  if (score == null || typeof score !== 'number') return {};
  const t = (score - 1) / 9; // 0..1
  const hue = 38 + t * 102;   // 38 (amber) -> 140 (green)
  const sat = 18;
  const light = 97;
  return { background: `linear-gradient(135deg, hsl(${hue}, ${sat}%, ${light}%) 0%, hsl(${hue}, ${sat}%, ${light - 4}%) 100%)` };
}

function HoroscopeDisplay({ horoscope }) {
  const { t, i18n } = useTranslation();
  const zodiacSymbol = ZODIAC_SYMBOLS[horoscope.zodiac_sign.toLowerCase()] || '✨';

  // Format date in the chosen language (locale from i18n)
  const locale = i18n.language || 'en';
  const formattedDate = new Date(horoscope.date + 'T00:00:00Z').toLocaleDateString(locale, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC'
  });

  const sections = horoscope.sections && typeof horoscope.sections === 'object' ? horoscope.sections : null;
  const scores = horoscope.scores && typeof horoscope.scores === 'object' ? horoscope.scores : null;
  const SCORE_MAX = 10;

  const renderScore = (key) => {
    const value = scores != null && typeof scores[key] === 'number' ? scores[key] : null;
    if (value == null) return null;
    const percent = Math.round((value / SCORE_MAX) * 100);
    return (
      <div className="horoscope-score-block" aria-label={`Score ${value} out of ${SCORE_MAX}`}>
        <div className="horoscope-score-bar-wrap">
          <div className="horoscope-score-bar" style={{ width: `${percent}%` }} />
        </div>
        <span className="horoscope-score-labels">
          <strong>{value}/{SCORE_MAX}</strong>
        </span>
      </div>
    );
  };

  return (
    <div className="horoscope-display">
      <div className="horoscope-header">
        <div className="zodiac-badge">
          <span className="zodiac-icon">{zodiacSymbol}</span>
          <h2>{t(`zodiac.${horoscope.zodiac_sign.toLowerCase()}`)}</h2>
        </div>
        <p className="horoscope-date">{formattedDate}</p>
      </div>
      <div className="horoscope-content">
        {sections ? (
          <div className="horoscope-sections">
            {SECTION_KEYS.filter(key => sections[key]).map(key => (
              <section
                key={key}
                className="horoscope-section"
                style={getSectionBackgroundStyle(scores != null ? scores[key] : null)}
              >
                <h3 className="horoscope-section-title">{t(`horoscopeSections.${key}`)}</h3>
                {renderScore(key)}
                <p className="horoscope-section-text">{sections[key]}</p>
              </section>
            ))}
          </div>
        ) : (
          <p className="horoscope-text">{horoscope.horoscope_text}</p>
        )}
      </div>
    </div>
  );
}

export default HoroscopeDisplay;

